import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { KrCard } from '../card/KrCard';

// Below this the text is too small to read, so the card falls back to clipping rather than shrinking further
const MIN_SCALE = 0.7;

export const StatCard = ({
  title,
  label,
  icon,
  val,
  isAmt,
  pctChange,
  vsLabel,
  isVs = true,
  cat,
  showPercent = true,
  center,
}) => {
  const theme = useTheme();
  const { containerRef, contentRef, scale } = useAutoFitScale();

  const isUp = pctChange > 0;
  const isDown = pctChange < 0;

  const pillBg =
    isUp ? alpha(theme.palette.success.main, 0.16)
    : isDown ? alpha(theme.palette.error.main, 0.16)
    : alpha(theme.palette.text.secondary, 0.12);

  const pillIcon =
    isUp ? fas.faArrowUp
    : isDown ? fas.faArrowDown
    : fas.faMinus;

  const amtText = isAmt ? formatAmt(val) : formatUnits(val);

  // Each line is measured on its own, so a secondary note does not shrink the line it qualifies
  const labelLines = (Array.isArray(label) ? label : [label]).filter(Boolean);

  return (
    <KrCard title={title} icon={icon}>
      <Box
        ref={containerRef}
        sx={{
          height: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: center ? 'center' : 'flex-end',
          overflow: 'hidden',
        }}
      >
        <Box ref={contentRef} sx={{ minWidth: 0, flexShrink: 0 }}>
          {labelLines.map((line, idx) => (
            <Typography
              key={idx}
              variant="subtitle2"
              sx={{
                mb: idx === labelLines.length - 1 ? 0.5 : 0,
                color: 'text.secondary',
                fontSize: `calc(${theme.typography.subtitle2.fontSize} * ${scale})`,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <span data-auto-fit>{line}</span>
            </Typography>
          ))}
          <Typography
            variant="h3"
            sx={{
              mb: 1,
              fontSize: {
                xs: `calc(1.75rem * ${scale})`,
                sm: `calc(2rem * ${scale})`,
                md: `calc(2.25rem * ${scale})`,
              },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <span data-auto-fit>{cat ?? amtText}</span>
          </Typography>
          {pctChange && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* bigger pill */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25, // wider
                  py: 0.5, // taller
                  bgcolor: pillBg,
                  borderRadius: 1.25,
                  fontSize: 14 * scale, // larger than caption
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                <FontAwesomeIcon icon={pillIcon} />
                {pctString(pctChange, showPercent)}
              </Box>

              {/* vs label matches pill’s size/weight (but neutral color) */}
              <Box
                sx={{
                  fontSize: 14 * scale,
                  // fontWeight: 600,
                  color: 'text.secondary',
                  lineHeight: 1.5,
                }}
              >
                {isVs ? `vs` : `for`} {vsLabel}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </KrCard>
  );
};

/* ---------- helpers ---------- */

// The tightest axis is what forces the squeeze, so the card types everything down by however much the widest
// line overruns the width available to it, or the stacked content overruns the height, and restores full size
// once it fits again. An extra label line therefore shrinks the whole card rather than spilling out of it.
const useAutoFitScale = () => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);

  const fit = () => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const availableWidth = container.clientWidth;
    const availableHeight = container.clientHeight;
    const measured = [...container.querySelectorAll('[data-auto-fit]')].map(
      (node) => node.getBoundingClientRect().width,
    );
    const widest = measured.length ? Math.max(...measured) : 0;
    const contentHeight = contentRef.current?.getBoundingClientRect().height ?? 0;
    if (!availableWidth || !widest) {
      return;
    }
    // Text is measured at the scale already applied, so it is normalized back to its unscaled size
    const widthFit = availableWidth / (widest / scaleRef.current);
    const heightFit =
      availableHeight && contentHeight ? availableHeight / (contentHeight / scaleRef.current) : Infinity;
    // Rounded down so the fixed spacing between the lines is never what tips the content back over the edge
    const next = Math.floor(Math.min(1, Math.max(MIN_SCALE, Math.min(widthFit, heightFit))) * 100) / 100;
    if (Math.abs(next - scaleRef.current) >= 0.01) {
      scaleRef.current = next;
      setScale(next);
    }
  };

  const fitRef = useRef(fit);
  fitRef.current = fit;

  useLayoutEffect(() => {
    fit();
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const observer = new ResizeObserver(() => fitRef.current());
    observer.observe(container);
    // Text measured before the web font loads is sized against the fallback font
    document.fonts?.ready.then(() => fitRef.current());
    return () => observer.disconnect();
  }, []);

  return { containerRef, contentRef, scale };
};

const formatAmt = (value) => {
  if (value == null || isNaN(value)) return '$-';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  const units = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' },
  ];

  let divisor = 1;
  let suffix = '';
  for (const u of units) {
    if (abs >= u.limit) {
      divisor = u.limit;
      suffix = u.suffix;
      break;
    }
  }

  const scaled = abs / divisor;
  let str = Number(scaled).toPrecision(3);
  str = String(+str); // drop trailing zeros / unnecessary decimals

  return `${sign}$${str}${suffix}`;
};

const formatUnits = (value) => {
  if (value == null || isNaN(value)) return '-';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  const units = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' },
  ];

  let divisor = 1;
  let suffix = '';
  for (const u of units) {
    if (abs >= u.limit) {
      divisor = u.limit;
      suffix = u.suffix;
      break;
    }
  }

  const scaled = abs / divisor;
  let str = Number(scaled).toPrecision(3);
  str = String(+str); // drop trailing zeros / unnecessary decimals

  return `${sign}${str}${suffix}`;
};

const pctString = (pct, showPercent = true) => {
  if (pct == null || isNaN(pct)) return showPercent ? '–%' : '–';
  const val = Math.round(pct * 100) / 100; // round to 2 decimals
  return showPercent ? `${val}%` : `${val}`;
};
